import React, { useCallback, useMemo, useState } from 'react';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import { Stack } from '@mui/material';
import { InfoDialog } from '../../common/dialogs/InfoDialog';
import { DocumentationContextProvider } from '../../documentation/context/DocumentationContext';
import { DocumentationFiltersProvider } from '../../documentation/selected-object/filters/DocumentationFiltersContext';
import { AgentSelectionType } from '../../../types/enums/AgentType';
import { ObjectTabsForAgent } from './ObjectTabsForAgent';
import { MetadataTableForAgent } from './MetadataTableForAgent';
import { getObjectNameColor } from '../../documentation/helpers';
import pluralize from 'pluralize';
import { AiAgent } from '../aiAgentsConsts';
import { SelectionItem } from './AgentContextSelectionModal';
import useAiAgents from '../useAiAgents';
import { getAgentType, getMetadataType, getObjectType } from './utils';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../../services/events';
import { usePermissions } from '../../../permissions/usePermissions';
import WithPermissions from '../../common/WithPermissions';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../../reducers/userInfoReducer';

interface AgentConfigurationsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  agent: AiAgent;
}

export const AgentConfigurationsDialog: React.FC<AgentConfigurationsDialogProps> = ({
  isOpen,
  onClose,
  agent,
}) => {
  const { updateAgentConfigurations, refreshAgentIssues } = useAiAgents();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extract existing configurations from agent
  const existingSelections = useMemo((): SelectionItem[] => {
    return agent.metadataElements || [];
  }, [agent]);

  const agentSelectionType = getAgentType(agent);
  const objectType = getObjectType(agent);
  const metadataType = getMetadataType(agent);
  const sendBiEvent = useSendBiEvent();
  const [selections, setSelections] = useState<SelectionItem[]>(existingSelections);
  const totalSelected = selections.length;

  const { evaluatePermissions } = usePermissions();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const [isAllowedToEditAgent] = evaluatePermissions([
    { permission: 'edit:ai-agent:configurations', crmOrgId: crmOrgId ?? '' },
  ]);

  const modalTitle = useMemo(() => {
    const isMetadataType = agentSelectionType === AgentSelectionType.METADATA_TYPES;
    const displayText = isMetadataType ? metadataType : objectType;
    const backgroundColor = isMetadataType
      ? colors.grey[200]
      : getObjectNameColor(objectType || '');

    return (
      <Typography variant="h3">
        Monitoring Configurations{' '}
        <span
          style={{
            backgroundColor,
            padding: '4px 8px',
            borderRadius: '2px',
          }}
        >
          {displayText}
        </span>
      </Typography>
    );
  }, [agentSelectionType, objectType, metadataType]);

  const handleSelectionChange = useCallback(
    (newSelections: SelectionItem[]) => {
      if (isSubmitting) return;
      setSelections(newSelections);
    },
    [isSubmitting],
  );

  const handleApplyChanges = useCallback(async () => {
    sendBiEvent({ name: AI_AGENTS_EVENTS.monitoringAgentConfigurationChange });
    setIsSubmitting(true);
    await updateAgentConfigurations(agent.id, selections);
    // Refresh issues table to reflect new configuration
    await refreshAgentIssues(agent.id);
    onClose();
    setIsSubmitting(false);
  }, [sendBiEvent, updateAgentConfigurations, agent.id, selections, refreshAgentIssues, onClose]);

  const renderButton = () => {
    const button = (
      <Button
        onClick={handleApplyChanges}
        disabled={isSubmitting || totalSelected === 0}
        loading={isSubmitting}
      >
        Apply changes
      </Button>
    );

    if (!isAllowedToEditAgent) {
      return (
        //in order to make button disabled state work split usages
        <WithPermissions
          accessValidation={{
            type: 'query',
            queries: [{ permission: 'edit:ai-agent:configurations', crmOrgId: crmOrgId ?? '' }],
          }}
          disabledTooltip="To edit agent's configurations, please contact your admin."
        >
          {button}
        </WithPermissions>
      );
    }

    return button;
  };

  const dialogActions = (
    <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
      <Typography variant="body" color={colors.grey[700]}>
        {pluralize('item', totalSelected, true) + ' selected'}
      </Typography>
      {renderButton()}
    </Stack>
  );

  const isObjectType = agentSelectionType === AgentSelectionType.OBJECT_TYPES;

  return (
    <InfoDialog
      showCloseButton={!isSubmitting}
      open={isOpen}
      handleClose={onClose}
      titleJsx={modalTitle}
      dialogActions={dialogActions}
      sx={{
        '& .MuiDialog-paper': {
          width: '90vw',
          maxWidth: '1400px',
          height: '85vh',
          maxHeight: 'none',
        },
      }}
    >
      <DocumentationContextProvider crmOrgId={agent.orgId}>
        <DocumentationFiltersProvider>
          {isObjectType ? (
            <ObjectTabsForAgent
              crmOrgId={agent.orgId}
              objectType={objectType!}
              onSelectionChange={handleSelectionChange}
              initialSelections={existingSelections}
            />
          ) : (
            <MetadataTableForAgent
              crmOrgId={agent.orgId}
              metadataType={metadataType!}
              onSelectionChange={handleSelectionChange}
              initialSelections={existingSelections}
            />
          )}
        </DocumentationFiltersProvider>
      </DocumentationContextProvider>
    </InfoDialog>
  );
};
